import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Github, ExternalLink, Layers, Code2, GitBranch, Zap, ShieldCheck, Network, KeyRound, Boxes } from 'lucide-react';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const commandText = 'docker compose -f quickstart/docker-compose.local.yml up';

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Typewriter effect for code snippet
  useEffect(() => {
    if (typedText.length < commandText.length) {
      const timeout = setTimeout(() => {
        setTypedText(commandText.slice(0, typedText.length + 1));
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [typedText]);

  const features = [
    {
      icon: <Layers className="w-8 h-8" />,
      title: 'Multi-Tenancy',
      description: 'Create isolated tenants with hard boundaries for identity routing and auth configuration.',
      color: 'cyan'
    },
    {
      icon: <Code2 className="w-8 h-8" />,
      title: 'Headless Architecture',
      description: 'Keep your own login, consent, and admin UX while Shyntr handles the protocol layer.',
      color: 'purple'
    },
    {
      icon: <GitBranch className="w-8 h-8" />,
      title: 'Protocol Translation',
      description: 'Bridge SAML, OIDC, and LDAP without rewrites, migrations, or protocol lock-in.',
      color: 'cyan'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Developer First',
      description: 'Keep your existing IAM or custom auth system and expose it through standard protocols.',
      color: 'purple'
    }
  ];

  const proofPoints = [
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: 'Zero Trust boundary',
      description: 'Identity is brokered at the edge, with tenant-level isolation and no shared auth state.'
    },
    {
      icon: <Network className="w-6 h-6" />,
      title: 'Standards first',
      description: 'Use standard SAML, LDAP, and OIDC flows to connect legacy enterprise apps and modern cloud apps.'
    },
    {
      icon: <KeyRound className="w-6 h-6" />,
      title: 'Headless control',
      description: 'Keep complete control over sign-in, consent, and user experience in your own product.'
    },
    {
      icon: <Boxes className="w-6 h-6" />,
      title: 'Tenant routing',
      description: 'Route each tenant to the right identity source without rewriting your app or provider.'
    }
  ];

  const useCases = [
    {
      title: 'SAML App to OIDC provider',
      description: 'Expose a legacy enterprise application through a modern OIDC-backed broker.'
    },
    {
      title: 'OIDC App to SAML IdP',
      description: 'Connect a modern application to an existing SAML identity provider with no app rewrite.'
    },
    {
      title: 'OIDC/SAML App to LDAP IdP',
      description: 'Connect applications to an existing Enterprise World'
    },
    {
      title: 'Custom User System',
      description: 'Keep your current user store or IAM and publish it through standard authentication protocols.'
    }
  ];

  return (
    <div className="landing-page">
      {/* Animated Background */}
      <div className="cyber-grid"></div>
      
      {/* Hero Section */}
      <section className={`hero-section ${isVisible ? 'visible' : ''}`}>
        <div className="container">
          <div className="hero-content">
            <img
                src="/mascot.svg"
                alt="Shyntr Mascot"
              className="mascot-logo"
            />
            <div className="hero-text">
              <h1 className="hero-headline">
                The Zero Trust <span className="gradient-text">Identity Broker</span>
              </h1>
              <p className="hero-subheadline">
                Bridge SAML, OIDC, and LDAP keep your existing IAM or custom auth system, and control your own login and consent UI.
              </p>
              <p className="hero-supporting-copy">
                Shyntr routes identity for multi-tenant products without rewrites, migrations, or protocol lock-in.
              </p>
              <div className="cta-buttons">
                <Button 
                  className="primary-cta"
                  onClick={() => window.open('https://github.com/Shyntr/shyntr', '_blank')}
                >
                  <Github className="w-5 h-5 mr-2" />
                  View on GitHub
                </Button>
                <Button
                  variant="outline"
                  className="secondary-cta"
                  onClick={() => window.open('https://docs.shyntr.com', '_blank')}
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Explore Docs
                </Button>
              </div>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="hero-visual">
            <div className="connection-hub">
              {/* Outer decorative circle */}
              <div className="outer-circle"></div>
              
              {/* Central triangular hub */}
              <div className="hub-center">
                <svg className="triangle-hub" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="triangleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#a855f7" />
                    </linearGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  <polygon 
                    points="50,15 85,75 15,75" 
                    fill="url(#triangleGradient)" 
                    stroke="#06b6d4" 
                    strokeWidth="2"
                    filter="url(#glow)"
                  />
                </svg>
              </div>
              
              {/* Connection lines */}
              <svg className="connection-lines" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg">
                <line x1="250" y1="60" x2="250" y2="200" className="conn-line line-1" />
                <line x1="80" y1="400" x2="200" y2="280" className="conn-line line-2" />
                <line x1="420" y1="400" x2="300" y2="280" className="conn-line line-3" />
              </svg>
              
              {/* Protocol boxes */}
              <div className="protocol-box box-custom">LDAP</div>
              <div className="protocol-box box-saml">SAML</div>
              <div className="protocol-box box-oidc">OIDC</div>
            </div>
          </div>
        </div>
      </section>

      {/* Construction Warning Banner */}
      {/*<section className="construction-section">
        <div className="container">
          <Card key={"construction-banner"} className={`construction-card construction-yellow`}>
            <CardContent className="construction-content">
              <div className={`construction-icon icon-yellow`}>
                <Construction className="w-8 h-8" />
              </div>
              <p className="construction-description">Stay Tuned! First Version Soon.</p>
            </CardContent>
          </Card>
        </div>
      </section>*/}

      {/* Value Proposition */}
      <section className="value-proposition">
        <div className="container">
          <div className="section-header section-header-tight">
            <h2 className="section-title">Built for identity brokering, not generic auth</h2>
            <p className="section-subtitle">Shyntr gives teams a controlled broker boundary for modern and legacy identity flows.</p>
          </div>
          <div className="value-grid">
            <div className="value-card problem-card">
              <div className="value-header">
                <div className="status-badge problem">Problem</div>
                <h3>Identity is split across systems</h3>
              </div>
              <p>Teams end up stitching together SAML apps, OIDC clients, and custom user stores with one-off integration code.</p>
            </div>
            <div className="value-card solution-card">
              <div className="value-header">
                <div className="status-badge solution">Solution</div>
                <h3>Shyntr brokers identity cleanly</h3>
              </div>
              <p>Shyntr sits between your apps and identity sources so you can keep your current stack and expose standard protocols safely.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Proof Section */}
      <section className="proof-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why teams use Shyntr</h2>
            <p className="section-subtitle">A compact broker layer for isolation, compliance, and control.</p>
          </div>
          <div className="proof-grid">
            {proofPoints.map((point, index) => (
              <Card key={index} className="proof-card">
                <CardContent className="proof-content">
                  <div className="proof-icon">{point.icon}</div>
                  <h3 className="proof-title">{point.title}</h3>
                  <p className="proof-description">{point.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="use-cases-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">When to use Shyntr</h2>
            <p className="section-subtitle">Concrete integration paths that stay within the current product scope.</p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <Card key={index} className="use-case-card">
                <CardContent className="use-case-content">
                  <h3 className="use-case-title">{useCase.title}</h3>
                  <p className="use-case-description">{useCase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Core capabilities</h2>
            <p className="section-subtitle">The smallest set of features needed to broker identity cleanly.</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <Card key={index} className={`feature-card feature-${feature.color}`}>
                <CardContent className="feature-content">
                  <div className={`feature-icon icon-${feature.color}`}>
                    {feature.icon}
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Code Snippet Section */}
      <section className="code-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Self-hosted quickstart</h2>
            <p className="section-subtitle">Start locally with the current Docker compose flow.</p>
          </div>
          <div className="terminal-window">
            <div className="terminal-header">
              <div className="terminal-buttons">
                <span className="terminal-button close"></span>
                <span className="terminal-button minimize"></span>
                <span className="terminal-button maximize"></span>
              </div>
              <div className="terminal-title">terminal</div>
            </div>
            <div className="terminal-body">
              <div className="terminal-line">
                <span className="terminal-prompt">$</span>
                <span className="terminal-command">{typedText}</span>
                <span className="terminal-cursor"></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <a 
              href="https://github.com/Shyntr/shyntr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link"
            >
              <Github className="w-5 h-5" />
              <span>GitHub</span>
            </a>
            <a 
              href="https://github.com/Shyntr/shyntr/blob/main/LICENSE" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link"
            >
              <span>Apache 2.0 License</span>
            </a>
            <a 
              href="https://buymeacoffee.com/nevzatcirak17" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link coffee-link"
            >
              <span>Buy Me a Coffee</span>
            </a>
            <a 
              href="https://nevzatcirak.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link"
            >
              <img 
                src="/nev.svg"
                alt="NEV Logo" 
                className="nev-logo"
              />
            </a>
          </div>
          <div className="footer-tagline">
            <p>Shyntr - Maneuvering Trust</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
